import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Zap, Sparkles, Gift, ArrowRight, Star, Clock, Tag, ChevronRight, Video } from 'lucide-react';
import axios from 'axios';
import PresetsCarousel from './PresetsCarousel';
import BlogCarousel from './BlogCarousel';
import './HomePage.css';
import { formatModelTokenPrice } from '../utils/textUtils';

// Вспомогательная функция для определения типа медиа
const isVideoFile = (url) => {
  if (!url) return false;
  const urlLower = url.toLowerCase();
  if (urlLower.includes('mime_type=video')) return true;
  return urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.webm') || urlLower.includes('.gif');
};

function HomePage({ currentBalance }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredModels, setFeaturedModels] = useState([]);

  useEffect(() => {
    fetchFeaturedModels();
  }, []);

  const fetchFeaturedModels = async () => {
    try {
      const response = await axios.get('/api/ai-models/featured/home', {
        params: { limit: 6 }
      });
      setFeaturedModels(response.data);
    } catch (error) {
      console.error('Error fetching featured models:', error);
    }
  };

  return (
    <div className="homepage">
      {/* Blog Carousel Section */}
      <BlogCarousel />

      {/* Presets Carousel */}
      <PresetsCarousel limit={6} showViewAll={true} />

      {/* Featured Models */}
      {featuredModels.length > 0 && (
        <div className="featured-models-section">
          <div className="section-header">
            <h2>Рекомендуемые модели</h2>
            <button 
              className="view-all-btn"
              onClick={() => navigate('/models')}
            >
              Смотреть все
              <ArrowRight size={16} />
            </button>
          </div>

          <div className="featured-models-grid">
            {featuredModels.map((model) => {
              const categories = {
                image_generation: 'Генерация',
                image_editing: 'Редактирование',
                enhancement: 'Улучшение',
                video_generation: 'Видео генерация',
                video_editing: 'Видео редактирование',
                music_generation: 'Музыка',
                speech_generation: 'Речь'
              };
              
              return (
                <div
                  key={model.id}
                  className={`featured-model-card ${model.is_featured ? 'featured' : ''}`}
                  onClick={() => navigate(`/models/${model.slug}`)}
                >
                  {model.is_featured && (
                    <div className="featured-badge">
                      <Star size={14} fill="currentColor" />
                      Рекомендуем
                    </div>
                  )}

                  <div className="model-cover">
                    {model.cover_image_url ? (
                      (() => {
                        const isVideo = isVideoFile(model.cover_image_url);
                        return isVideo ? (
                          <video 
                            src={model.cover_image_url} 
                            muted
                            loop
                            playsInline
                            autoPlay
                            preload="auto"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onLoadedData={(e) => {
                              // Принудительно запускаем воспроизведение после загрузки
                              e.target.play().catch(() => {
                                // Autoplay prevented - игнорируем
                              });
                            }}
                          />
                        ) : (
                          <img 
                            src={model.cover_image_url} 
                            alt={model.display_name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        );
                      })()
                    ) : null}
                    <div className="cover-placeholder" style={{display: model.cover_image_url ? 'none' : 'flex'}}>
                      <Zap size={48} />
                    </div>
                    
                    <div className="model-overlay">
                      <button className="select-btn">
                        Выбрать модель
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="model-info">
                    <div className="model-header">
                      <h3 className="model-name">{model.display_name}</h3>
                      <div className="model-category">
                        {categories[model.category] || model.category}
                      </div>
                    </div>

                    <p className="model-description">
                      {model.short_description}
                    </p>

                    <div className="model-tags">
                      {model.tags && model.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="tag">
                          <Tag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="model-footer">
                      <div className="model-stats">
                        <div className="stat">
                          <Zap size={14} />
                          <span className="cost">{formatModelTokenPrice(model)}</span>
                        </div>
                        <div className="stat">
                          <Clock size={14} />
                          <span>~{model.estimated_time_seconds}с</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="featured-models-footer">
            <button
              type="button"
              className="all-models-btn"
              onClick={() => navigate('/models')}
            >
              Все модели
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default HomePage;
